import { useState, useRef, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  Autocomplete,
  CircularProgress,
  Chip,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useDispatch, useSelector } from "react-redux";
import { sendSmsOtp } from "../../State/Auth/Action";
import { getStatesWithCities } from "../../State/State/Action";
import api from "../../services/api";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import { useMyContext } from "../../store/ContextApi";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

const Login = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [jwtToken, setJwtToken] = useState("");
  const { setToken, token } = useMyContext();
  const [showCustomerName, setShowCustomerName] = useState(false);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("customer");
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);

  const { statesWithCities = [], loading: statesLoading = false } = useSelector(
    (store) => store.stateResponse || {}
  );

  // Flatten cities with state information for Autocomplete
  const cityOptions = statesWithCities.flatMap((state) =>
    (state.cities || []).map((city) => ({
      ...city,
      stateName: state.stateName,
      stateId: state.stateId,
      label: `${city.cityName}, ${state.stateName}`,
    }))
  );

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const [timeLeft, setTimeLeft] = useState(120);
  const [timerExpired, setTimerExpired] = useState(false);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (/^\d{0,10}$/.test(value)) {
      setPhone(value);
    }
  };

  const showAlert = (message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleCheckboxChange = (event) => {
    setRole(event.target.checked ? "dealer" : "customer");
  };

  const handleGetOtp = () => {
    if (phone.length !== 10) {
      showAlert("Please enter a valid 10-digit phone number");
      return;
    }
    dispatch(sendSmsOtp(phone));
    setOpen(true);
  };

  const handleResendOtp = () => {
    dispatch(sendSmsOtp(phone));
    setTimeLeft(120);
    setTimerExpired(false);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1]?.current?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      showAlert("Please enter a valid 4-digit OTP");
      return;
    }
    const otpLoginRequest = { phoneNumber: phone, otp: otpValue };
    otpSignIn(otpLoginRequest);
  };

  const otpSignIn = async (input) => {
    try {
      const response = await api.post("/auth/public/otp-signin", input);
      toast.success("Login Successful");
      if (response.status === 200 && response.data.jwtToken) {
        setJwtToken(response.data.jwtToken);
        const decodedToken = jwtDecode(response.data.jwtToken);
        handleSuccessfulLogin(response.data.jwtToken, decodedToken);
      } else {
        toast.error(
          "Login failed. Please check your credentials and try again."
        );
      }
    } catch (error) {
      if (error?.response?.data?.userExists === false) {
        setShowCustomerName(true);
      } else {
        toast.error("Invalid credentials");
      }
    }
  };

  const handleSave = async () => {
    const otpValue = otp.join("");
    const input = { 
      phoneNumber: phone, 
      otp: otpValue, 
      fullName, 
      role,
      cityId: selectedCity?.cityId || selectedCityId || null,
      stateId: selectedCity?.stateId || null
    };
    const response = await api.post("/auth/public/signup-by-phone", input);

    if (response.status === 200 && response.data.jwtToken) {
      setJwtToken(response.data.jwtToken);
      const decodedToken = jwtDecode(response.data.jwtToken);
      setSelectedCity(null);
      setSelectedCityId("");
      handleSuccessfulLogin(response.data.jwtToken, decodedToken);
    } else {
      toast.error(
        "Unable to register. Please check your credentials and try again."
      );
    }
  };

  const handleSuccessfulLogin = (token, decodedToken) => {
    const user = {
      username: decodedToken.sub,
      roles: decodedToken.roles ? decodedToken.roles.split(",") : [],
    };
    localStorage.setItem("JWT_TOKEN", token);
    localStorage.setItem("USER", JSON.stringify(user));
    setToken(token);
    navigate("/home");
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  };

  useEffect(() => {
    let timer;
    if (open) {
      setTimeLeft(120);
      setTimerExpired(false);
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setTimerExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (showCustomerName) {
      dispatch(getStatesWithCities());
    }
  }, [showCustomerName, dispatch]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        <Card 
          className="card shadow-strong border-0 overflow-hidden"
          sx={{
            borderRadius: "24px",
            backdropFilter: "blur(10px)",
            background: "rgba(255, 255, 255, 0.95)",
          }}
        >
          <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-8 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
            
            <motion.div
              initial={{ scale: 0.8, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative z-10"
            >
              <LockOpenIcon className="text-white text-3xl" />
            </motion.div>
            <Typography 
              variant="h4" 
              className="text-white font-bold mb-2 relative z-10"
              sx={{ 
                fontSize: "1.875rem",
                fontWeight: 700,
                letterSpacing: "-0.025em"
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body2" 
              className="text-white/90 relative z-10"
              sx={{ fontSize: "0.95rem" }}
            >
              Sign in to your account to continue
            </Typography>
          </div>
          
          <CardContent className="p-8 bg-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Phone Number
                </label>
                <div className="relative group">
                  <PhoneAndroidIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primary-500 w-6 h-6 z-10 transition-colors duration-200 group-focus-within:text-primary-600" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter your 10-digit phone number"
                    maxLength={10}
                    className="w-full pl-14 pr-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-200 outline-none bg-gray-50 focus:bg-white"
                  />
                  {phone && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                    >
                      <CheckCircleIcon className="text-green-500 w-5 h-5" />
                    </motion.div>
                  )}
                </div>
                {phone && phone.length < 10 && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-amber-600 mt-2 ml-1"
                  >
                    {10 - phone.length} more digits required
                  </motion.p>
                )}
              </div>

              <motion.div 
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }}
                className="pt-2"
              >
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGetOtp}
                  disabled={phone.length !== 10}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: "12px",
                    textTransform: "none",
                    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.39)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                      boxShadow: "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#e5e7eb",
                      color: "#9ca3af",
                      boxShadow: "none",
                    },
                    transition: "all 0.3s ease",
                  }}
                  startIcon={<LockOpenIcon />}
                >
                  Get OTP
                </Button>
              </motion.div>

              <div className="text-center pt-4">
                <Typography 
                  variant="body2" 
                  className="text-gray-500 text-sm"
                  sx={{ lineHeight: 1.6 }}
                >
                  By continuing, you agree to our{" "}
                  <a 
                    href="/terms" 
                    className="text-primary-600 hover:text-primary-700 font-semibold transition-colors duration-200 underline-offset-2 hover:underline"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a 
                    href="/privacy" 
                    className="text-primary-600 hover:text-primary-700 font-semibold transition-colors duration-200 underline-offset-2 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </Typography>
              </div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
            
            <motion.div
              initial={{ scale: 0.8, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.1, duration: 0.4, type: "spring" }}
              className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10"
            >
              <AccessTimeIcon className="text-white text-2xl" />
            </motion.div>
            <Typography 
              variant="h5" 
              className="text-white font-bold relative z-10"
              sx={{ 
                fontSize: "1.5rem",
                fontWeight: 700,
                mb: 1
              }}
            >
              Verify OTP
            </Typography>
            <Typography 
              variant="body2" 
              className="text-white/90 relative z-10"
              sx={{ fontSize: "0.95rem" }}
            >
              Enter the code sent to your phone
            </Typography>
          </div>
          
          <DialogContent className="p-8 bg-white">
            <div className="text-center space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Typography className="text-gray-600 mb-2 text-sm">
                  We sent a verification code to
                </Typography>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-xl">
                  <PhoneAndroidIcon className="text-primary-600" sx={{ fontSize: 20 }} />
                  <Typography className="font-bold text-primary-700 text-lg">
                    +91 {phone}
                  </Typography>
                </div>
              </motion.div>

              <motion.div 
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className={`px-5 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  timerExpired 
                    ? "bg-red-50 text-red-700 border-2 border-red-200" 
                    : "bg-primary-50 text-primary-700 border-2 border-primary-200"
                }`}>
                  <div className="flex items-center gap-2">
                    <AccessTimeIcon sx={{ fontSize: 20 }} />
                    <Typography className="font-semibold">
                      {timerExpired
                        ? "Time expired! Request new OTP"
                        : `Time left: ${formatTime(timeLeft)}`}
                    </Typography>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="flex gap-4 justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {otp.map((digit, index) => (
                  <motion.input
                    key={index}
                    type="tel"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    ref={otpRefs[index]}
                    maxLength={1}
                    whileFocus={{ scale: 1.1 }}
                    className="w-16 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-200 bg-gray-50 focus:bg-white shadow-sm focus:shadow-md"
                  />
                ))}
              </motion.div>

              {timerExpired && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResendOtp}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Resend OTP
                  </Button>
                </motion.div>
              )}
            </div>
          </DialogContent>
          
          <DialogActions className="p-6 pt-0 gap-3 bg-gray-50">
            <Button 
              onClick={() => setOpen(false)}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1.5,
                color: "gray.700",
                "&:hover": {
                  bgcolor: "gray.100",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleVerifyOtp}
              disabled={timerExpired}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.39)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                  boxShadow: "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#e5e7eb",
                  color: "#9ca3af",
                  boxShadow: "none",
                },
                transition: "all 0.3s ease",
              }}
            >
              Verify OTP
            </Button>
          </DialogActions>
        </motion.div>
      </Dialog>

      <Dialog
        open={showCustomerName}
        onClose={() => {
          setShowCustomerName(false);
          setSelectedCity(null);
          setSelectedCityId("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }
        }}
      >
        <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-6 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <DialogTitle 
            className="text-white font-bold relative z-10"
            sx={{ 
              fontSize: "1.5rem",
              fontWeight: 700,
              pb: 1
            }}
          >
            Complete Your Profile
          </DialogTitle>
          <Typography 
            variant="body2" 
            className="text-white/90 relative z-10"
            sx={{ fontSize: "0.9rem" }}
          >
            Please provide your details to continue
          </Typography>
        </div>
        <DialogContent className="flex flex-col gap-5 p-6 bg-white">
          <TextField
            variant="outlined"
            label="Enter Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            inputProps={{
              maxLength: 30,
            }}
            fullWidth
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                },
                "&.Mui-focused": {
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                },
              },
            }}
          />
          
          <Autocomplete
            fullWidth
            options={cityOptions}
            value={selectedCity}
            onChange={(event, newValue) => {
              setSelectedCity(newValue);
              setSelectedCityId(newValue?.cityId || "");
            }}
            loading={statesLoading}
            groupBy={(option) => option.stateName}
            getOptionLabel={(option) => option.label || `${option.cityName}, ${option.stateName}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select City"
                required
                placeholder="Search or select your city"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <LocationOnIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {statesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: "primary.50",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "primary.100",
                  },
                }}
              >
                <LocationOnIcon sx={{ color: "primary.main", fontSize: 20 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.cityName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {option.stateName}
                  </Typography>
                </Box>
              </Box>
            )}
            renderGroup={(params) => (
              <li key={params.key}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "primary.main",
                    color: "white",
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <MapIcon sx={{ fontSize: 18 }} />
                  {params.group}
                </Box>
                <Box component="ul" sx={{ p: 0, m: 0 }}>
                  {params.children}
                </Box>
              </li>
            )}
            noOptionsText={
              statesLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography>Loading cities...</Typography>
                </Box>
              ) : (
                "No cities found"
              )
            }
            sx={{
              "& .MuiAutocomplete-popper": {
                "& .MuiPaper-root": {
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            }}
            disabled={statesLoading && cityOptions.length === 0}
          />

          <Box className="pt-2 flex items-center justify-center">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleCheckboxChange}
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": { 
                        color: "primary.main",
                      },
                      transform: "scale(1.2)",
                      "& .MuiSvgIcon-root": {
                        fontSize: 28,
                      },
                    }}
                  />
                }
                label={
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: "#2563eb",
                    }}
                  >
                    Are you a Bike Dealer?
                  </span>
                }
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3 bg-gray-50">
          <Button 
            onClick={() => {
              setShowCustomerName(false);
              setSelectedCity(null);
              setSelectedCityId("");
            }}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.5,
              color: "gray.700",
              "&:hover": {
                bgcolor: "gray.100",
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            disabled={!fullName?.trim() || !selectedCity}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.39)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                boxShadow: "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#e5e7eb",
                color: "#9ca3af",
                boxShadow: "none",
              },
              transition: "all 0.3s ease",
            }}
          >
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={alertOpen} 
        onClose={() => setAlertOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
          }
        }}
      >
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center">
          <DialogTitle 
            className="text-white font-bold"
            sx={{ 
              fontSize: "1.25rem",
              fontWeight: 700,
              pb: 0
            }}
          >
            Alert
          </DialogTitle>
        </div>
        <DialogContent className="p-6">
          <Typography 
            className="text-gray-700"
            sx={{ fontSize: "1rem" }}
          >
            {alertMessage}
          </Typography>
        </DialogContent>
        <DialogActions className="p-6 pt-0">
          <Button 
            onClick={() => setAlertOpen(false)} 
            variant="contained"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Login;
